import React, { useState } from "react";
import "./workExperience.css";
import { useNavigate } from "react-router-dom";
import {
  resetOnboardingInfo,
  updateOnboardingInfo,
} from "../../redux/attorneyOnboardingSlice";
import { useDispatch, useSelector } from "react-redux";
import BackIcon from "./assets/backIcon.png";
import NarrowDropdown2 from "../../components/input/new/narrowDropdown2";
import NarrowInputField from "../../components/input/new/narrowInputField";
import AddIcon from "./assets/addIcon.png";
import NarrowDropdownHierarchy from "../../components/input/new/narrowDropdownHierarchy";
import StepFooter from "./stepFooter";
import RadioButton from "../../components/input/new/radioButton";
import { positions } from "./schoolData";
import TextAreaWithCount from "../../components/input/new/textAreaWithCount";

const WorkExperience = () => {
  const userId = useSelector(
    (state) => state.attorneyData.attorneyData?.user_id
  );

  const [workHistory, setWorkHistory] = useState([]);
  const [yoe, setYOE] = useState("");

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 70 }, (_, index) => {
    const year = currentYear - index;
    return { label: String(year), value: String(year) };
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const goBackToProfessionals = () => {
    dispatch(resetOnboardingInfo());
    navigate(`/attorney/${userId}/home`);
  };

  const handleNext = () => {
    const currentJob = workHistory.find((w) => w.present);
    const role = currentJob ? currentJob.position : "";

    dispatch(updateOnboardingInfo({ workHistory, role, yoe }));
  };

  const addWorkHistory = () => {
    setWorkHistory((prev) => [
      ...prev,
      {
        firmName: "",
        position: "",
        startYear: "",
        endYear: "",
        description: "",
        // Set the first added row as present by default
        present: prev.length === 0,
      },
    ]);
  };

  const removeWorkHistory = (index) => {
    setWorkHistory((prev) => {
      const newArray = prev.filter((_, i) => i !== index);
      // If the removed row was marked as present and there are remaining rows,
      // mark the first one as present.
      if (prev[index].present && newArray.length > 0) {
        newArray[0].present = true;
      }
      return newArray;
    });
  };

  const handleWorkHistoryChange = (index, field, value) => {
    setWorkHistory((prev) => {
      const newArray = [...prev];
      newArray[index] = { ...newArray[index], [field]: value };
      return newArray;
    });
  };

  const handlePresentChange = (index) => {
    setWorkHistory((prev) =>
      prev.map((item, i) => ({ ...item, present: i === index }))
    );
  };

  // Disable the footer if no work experience is added or if any required field is missing.
  // Required fields: firmName, position, startYear, endYear.
  const isFooterDisabled =
    workHistory.length === 0 ||
    workHistory.some(
      (work) =>
        !work.firmName.trim() ||
        !work.position ||
        !work.startYear ||
        !work.endYear
    );

  return (
    <div className="work-container">
      <div className="category-profile-types">
        <div style={{ display: "flex", flexDirection: "column", width: "40%" }}>
          <div className="category-breadcrumb" onClick={goBackToProfessionals}>
            <img
              src={BackIcon}
              alt="Back Icon"
              style={{ width: "24px", height: "24px" }}
            />
            <a className="category-back-link">Back to professionals</a>
          </div>

          <h1 className="work-title">Work Experience</h1>
          <p className="work-second-title">Work History</p>
          <p className="work-subtitle">
            This should include your past roles and firms in chronological
            order.
          </p>

          <div style={{ height: "1.5rem" }} />

          {workHistory.map((work, index) => (
            <div key={index} className="work-row">
              <div className="work-row-header">
                <span className="work-number-title">
                  Experience {index + 1}
                </span>
                <button
                  className="delete-work-btn"
                  onClick={() => removeWorkHistory(index)}
                  title="Delete experience"
                >
                  Delete
                </button>
              </div>

              <NarrowInputField
                name="Company / Firm Name"
                title="Company / Firm Name"
                placeholder="Enter a firm name"
                isRequired={true}
                value={work.firmName}
                onChange={(e) =>
                  handleWorkHistoryChange(index, "firmName", e.target.value)
                }
                halfWidth={false}
              />

              <NarrowDropdownHierarchy
                title="Position / Title"
                placeholder="Select your position"
                items={positions}
                isRequired={true}
                value={work.position}
                onChange={(val) =>
                  handleWorkHistoryChange(index, "position", val)
                }
                multiple={false}
                hierarchical={false}
              />

              <div
                style={{ display: "flex", alignItems: "center", gap: "20px" }}
              >
                <NarrowDropdown2
                  title="Start year"
                  placeholder="Select"
                  items={yearOptions}
                  isRequired={true}
                  value={work.startYear}
                  onChange={(value) =>
                    handleWorkHistoryChange(index, "startYear", value)
                  }
                  multiple={false}
                  halfWidth={true}
                />
                <NarrowDropdown2
                  title="End year"
                  placeholder="Select"
                  items={yearOptions}
                  isRequired={true}
                  value={work.endYear}
                  onChange={(value) =>
                    handleWorkHistoryChange(index, "endYear", value)
                  }
                  multiple={false}
                  halfWidth={true}
                />
              </div>

              <div className="primary-selector">
                <RadioButton
                  label="Set as present employment"
                  name="presentEmployment"
                  value={index}
                  checked={work.present}
                  onChange={() => handlePresentChange(index)}
                />
              </div>

              <TextAreaWithCount
                maxChars={2000}
                placeholder="Tell us about your role..."
                value={work.description}
                onChange={(e) =>
                  handleWorkHistoryChange(index, "description", e.target.value)
                }
              />

              <div style={{ height: "1.5rem" }} />

              <hr className="h-rule" />
            </div>
          ))}

          <button className="work-add-button" onClick={addWorkHistory}>
            <img
              src={AddIcon}
              style={{ width: "16px", height: "16px" }}
              alt="Add"
            />
            Add work history
          </button>

          <hr className="h-rule" />

          <NarrowInputField
            name="Years of Experience"
            title="Years of Experience"
            placeholder="Enter your total years of experience"
            isRequired={true}
            value={yoe}
            onChange={(e) => {
              setYOE(e.target.value);
            }}
            halfWidth={false}
          />
        </div>
      </div>

      <StepFooter
        currentStep={7}
        totalSteps={9}
        location="/attorney/setup/case-highlights"
        onNext={handleNext}
        disabled={isFooterDisabled} // Continue button disabled if required fields are missing
      />
    </div>
  );
};

export default WorkExperience;
